import classNames from "classnames";
import { useCallback, useContext, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { CloseIcon } from "@icons";
import { useRemoveFinancialDocumentMutation } from "@mutations/financialDocument";
import { FinancialDocument } from "../../common/types/financialDocument";
import { IntegrationAccount } from "../../common/types/integrationAccount";
import { GridTableRow } from "../general/Tables/GridTable/GridTable";
import { DocumentName } from "../documents/DocumentName";
import { EditableTableCell } from "../accounting/editableTable/EditableTableCell";
import editableTableStyles from "../accounting/editableTable/EditableTable.module.scss";
import { EntitySelect } from "../entity/EntitySelect";
import { useEntities } from "../../hooks/useEntities";
import { Entity } from "../../common/types/entity";
import {
    IntegrationAccountUpdateBody,
    useIntegrationAccountUpdateMutation,
} from "../../api/integrationAccount.api";
import { EditableTableCellString } from "../accounting/editableTable/EditableTableCellString";
import { FinancialAccountFinancialTypeSelect } from "../financialAccount/FinancialAccountFinancialTypeSelect";
import { FinancialAccountType } from "../../common/types/financialAccount";
import { Button } from "../general/Button/Button";
import { fullDateFormat } from "../../common/helpers/date";
import { useFinancialAccounts } from "../../hooks/useFinancialAccounts";
import { ImporterDocumentStatus } from "./ImporterDocumentStatus";
import styles from "./ImporterPage.module.scss";
import { ImporterDocumentRow } from "./types";
import { ImporterContext } from "./importer.context";
import { ImporterAccountPicker } from "./ImporterAccountPicker";

interface Props {
    rowNumber: number;
    documentRow: ImporterDocumentRow;
    financialDocument: FinancialDocument;
    integrationAccount: IntegrationAccount;
}

export const ImporterTableAccountRow: React.FC<Props> = ({
    rowNumber,
    documentRow,
    financialDocument,
    integrationAccount,
}) => {
    const {
        updateDocumentRow,
        setPreviewedRow,
        previewedRow,
        removeDocumentRow,
    } = useContext(ImporterContext);
    const isPreviewed =
        previewedRow?.financialDocument.id === financialDocument.id;
    const financialAccounts = useFinancialAccounts({
        excludeMockEntities: true,
    });
    const selectedFinancialAccount = useMemo(
        () =>
            financialAccounts.find(
                (account) =>
                    account.id ===
                    integrationAccount.originalFinancialAccountId,
            ),
        [financialAccounts, integrationAccount.originalFinancialAccountId],
    );
    const entities = useEntities();
    const selectedEntityId =
        selectedFinancialAccount?.entityId ??
        integrationAccount.originalEntityId;
    const selectedEntity = useMemo(
        () => entities.find((entity) => entity.id === selectedEntityId),
        [selectedEntityId, entities],
    );

    const updateIntegrationAccount = useIntegrationAccountUpdateMutation({
        integrationAccount,
        onMutate: (update) => {
            updateDocumentRow(documentRow.id, {
                integrationAccount: {
                    ...integrationAccount,
                    ...update,
                },
            });
        },
        onError: (previousAccount) => {
            updateDocumentRow(documentRow.id, {
                integrationAccount: previousAccount,
            });
        },
    });

    const removeFinancialDocument = useRemoveFinancialDocumentMutation();

    const removeDocument = useCallback(() => {
        removeFinancialDocument.mutate(financialDocument);
        removeDocumentRow(documentRow);
    }, [
        removeFinancialDocument,
        financialDocument,
        removeDocumentRow,
        documentRow,
    ]);

    const updateAccount = useCallback(
        (payload: IntegrationAccountUpdateBody) => {
            updateIntegrationAccount.mutate(payload);
        },
        [updateIntegrationAccount],
    );

    const onEntityChanged = useCallback(
        (entity: Entity | undefined) => {
            updateAccount({
                originalEntityId: entity?.id ?? null,
            });
        },
        [updateAccount],
    );

    const onInstitutionNameChanged = useCallback(
        (institutionName: string) => {
            updateAccount({
                originalInstitutionName: institutionName
                    ? institutionName
                    : null,
            });
        },
        [updateAccount],
    );

    const onAccountTypeChanged = useCallback(
        (accountType: FinancialAccountType) => {
            updateAccount({
                originalFinancialType: accountType,
            });
        },
        [updateAccount],
    );

    const openingDate = useMemo(
        () =>
            financialDocument.openingDate
                ? parseISO(financialDocument.openingDate)
                : undefined,
        [financialDocument.openingDate],
    );
    const closingDate = useMemo(
        () =>
            financialDocument.closingDate
                ? parseISO(financialDocument.closingDate)
                : undefined,
        [financialDocument.closingDate],
    );

    const institutionName = selectedFinancialAccount
        ? selectedFinancialAccount.institutionName
        : integrationAccount.originalInstitutionName;
    const financialAccountType = selectedFinancialAccount
        ? selectedFinancialAccount.financialType
        : integrationAccount.originalFinancialType;

    return (
        <GridTableRow
            columnBorders
            className={classNames(styles.row, {
                [styles.previewedRow]: isPreviewed,
            })}
            onClick={(e) => e.stopPropagation()}
            clickable={false}
        >
            <GridTableRow columnBorders className={styles.stickyLeft}>
                <div className={styles.rowNumber}>{rowNumber}</div>
                <div>
                    <Button
                        variant="link"
                        onClick={() =>
                            setPreviewedRow({
                                id: documentRow.id,
                                financialDocument,
                                integrationAccount,
                            })
                        }
                        className={styles.documentNameButton}
                    >
                        <DocumentName fileName={financialDocument.fileName} />
                    </Button>
                </div>
            </GridTableRow>
            <div>
                <ImporterDocumentStatus documentRow={documentRow} />
            </div>
            <EditableTableCellString
                value={institutionName ?? ""}
                onChange={onInstitutionNameChanged}
                className={styles.editableCell}
                readOnly={Boolean(selectedFinancialAccount)}
            />
            <EditableTableCell
                className={classNames(
                    editableTableStyles.dropdownEditableCell,
                    styles.editableCell,
                )}
            >
                <ImporterAccountPicker
                    integrationAccount={integrationAccount}
                    selectedFinancialAccount={selectedFinancialAccount}
                    onChange={updateAccount}
                />
            </EditableTableCell>
            <EditableTableCell
                className={classNames(
                    editableTableStyles.dropdownEditableCell,
                    styles.editableCell,
                )}
            >
                <FinancialAccountFinancialTypeSelect
                    value={financialAccountType ?? undefined}
                    onChange={onAccountTypeChanged}
                    dropdownKey={`${documentRow.id}-financial-type`}
                    size="sm"
                    readOnly={Boolean(selectedFinancialAccount)}
                />
            </EditableTableCell>
            <div>
                {openingDate && closingDate
                    ? `${format(openingDate, fullDateFormat)} - ${format(closingDate, fullDateFormat)}`
                    : ""}
            </div>
            <EditableTableCell
                className={classNames(
                    editableTableStyles.dropdownEditableCell,
                    styles.editableCell,
                )}
            >
                <EntitySelect
                    entities={entities}
                    selectedEntity={selectedEntity}
                    onChange={onEntityChanged}
                    dropdownKey={`${documentRow.id}-entity`}
                    size="sm"
                    readOnly={Boolean(selectedFinancialAccount)}
                />
            </EditableTableCell>
            <GridTableRow columnBorders className={styles.stickyRight}>
                <div className={styles.actionsColumn}>
                    {documentRow.financialDocument?.numTransactions}

                    <div className={styles.documentActions}>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                setPreviewedRow({
                                    id: documentRow.id,
                                    financialDocument,
                                    integrationAccount,
                                })
                            }
                        >
                            Review
                        </Button>
                        <Button
                            variant="tertiary"
                            size="sm"
                            icon
                            onClick={removeDocument}
                            tooltip="Remove document"
                        >
                            <CloseIcon />
                        </Button>
                    </div>
                </div>
            </GridTableRow>
        </GridTableRow>
    );
};
